import enLocale from '@/modules/common/filters/businessDateFormatsLocales/en.json';

const DAY_NAMES = enLocale.date.day_names;

/**
 * Convert a 24h "HH:MM" slot string to the account's preferred format.
 * When timeFormat is '12h' returns "h:MM AM/PM", otherwise the original 24h string.
 * Non-time input (empty/malformed) is returned untouched so upstream filtering still works.
 *
 * @param {string} rawTime
 * @param {boolean} use12h
 * @returns {string}
 */
function formatSlotTime(rawTime, use12h) {
  const trimmed = (rawTime ?? '').trim();
  if (!use12h) return trimmed;
  const match = /^(\d{1,2}):(\d{2})/.exec(trimmed);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = hours < 12 ? 'AM' : 'PM';
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${meridiem}`;
}

/**
 * Format time_slots_by_day (from GET v2/weekly_availabilities?general_availability=true)
 * to a single human-readable string. Plan §2.1; same format as webaiwidgetmgr.
 * Day order: Sunday (0) … Saturday (6). Closed = "Closed"; open = "DayName: 09:00–17:00".
 * Collapses consecutive days with same slots (e.g. "Monday–Friday: 09:00–17:00").
 * Each day or group of days is on its own line (newline-separated).
 *
 * The API always returns 24h HH:MM strings; the account's 12h/24h preference
 * (BusinessStore attributes.time_format) is applied here via formatSlotTime.
 *
 * @param {Array<{ day: number, enabled: boolean, slots: Array<{ start_time: string, end_time: string }> }>} timeSlotsByDay
 * @param {('12h'|'24h'|null|undefined)} timeFormat account time format; defaults to 24h
 * @returns {string}
 */
function formatTimeSlotsByDayToHumanReadable(timeSlotsByDay, timeFormat) {
  const use12h = timeFormat === '12h';
  const sorted = [...(timeSlotsByDay ?? [])].sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
  const parts = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const d = sorted[i];
    const dayNum = d.day ?? i;
    const dayName = DAY_NAMES[dayNum] ?? `Day ${dayNum}`;
    const slotStr = !d.enabled || !Array.isArray(d.slots) || d.slots.length === 0
      ? 'Closed'
      : d.slots
        .map((s) => `${formatSlotTime(s.start_time, use12h)}–${formatSlotTime(s.end_time, use12h)}`)
        .filter((s) => s !== '–')
        .join(', ');
    if (parts.length > 0 && parts[parts.length - 1].endsWith(`: ${slotStr}`)) {
      const last = parts[parts.length - 1];
      const dayPart = (last.split(':')[0] || '').trim();
      const firstDayName = dayPart.includes('–')
        ? (dayPart.split('–')[0] || dayPart).trim()
        : dayPart;
      parts[parts.length - 1] = slotStr === 'Closed'
        ? `${firstDayName}–${dayName}: Closed`
        : `${firstDayName}–${dayName}: ${slotStr}`;
    } else {
      parts.push(`${dayName}: ${slotStr}`);
    }
  }
  return parts.join('\n');
}

export default formatTimeSlotsByDayToHumanReadable;
